<template>
  <div>
    <order-details
      v-if="step === 'details'"
      :order="internalOrder"
      @handle-order="handleOrder"
      @on-patient-press="onPatientPress"
    />
    <order-operator
      v-if="step === 'operator'"
      :order="internalOrder"
      :loading="editingOrder"
      @update-operator-code="updateOperatorCode"
      @back="setStep('details')"
    />
    <order-date
      v-if="step === 'date'"
      :order="internalOrder"
      @validate="validateDate"
      @back="setStep('operator')"
    />
    <order-comment
      v-if="step === 'comment'"
      :order="internalOrder"
      :loading="editingOrder"
      @validate="validateOrder"
      @back="setStep('date')"
    />
    <rejected-order-comment
      v-if="step === 'reject-comment'"
      :loading="editingOrder"
      @validate="rejectOrder"
      @back="setStep('operator')"
    />
  </div>
</template>


<script lang="ts" setup>
import {ref} from "vue";
import Customer from "@/customer/interfaces/Customer";
import Order from "@/order/interfaces/Order";
import {orderApi} from "@/container";
import dayjs from "dayjs";
import OrderDetails from "@/order/components/OrderDetails.vue";
import OrderOperator from "@/order/components/OrderOperator.vue";
import OrderDate from "@/order/components/OrderDate.vue";
import OrderComment from "@/order/components/OrderComment.vue";
import RejectedOrderComment from "@/order/components/RejectedOrderComment.vue";

const props = defineProps<{
    order: Order
}>();

const emits = defineEmits<{
    (e: 'goToPatient', patient: Customer): void;
    (e: 'orderHandled'): void;
}>();

const step = ref<"details" | "operator" | "date" | "comment" | "reject-comment">('details');
const editingOrder = ref(false);
const isRefused = ref(false);
const internalOrder = ref(props.order);

/**
 * Set the component step.
 */
const setStep = (name: "details" | "operator" | "date" | "comment" | "reject-comment") => {
    step.value = name;
};

/**
 * Emit the event to redirect to patient details.
 */
const onPatientPress = (patient: Customer) => {
    emits('goToPatient', patient);
};

/**
 * Handle the order acceptance or refusal.
 *
 * @param accept
 */
const handleOrder = (accept: boolean) => {
    if (!accept) {
        isRefused.value = true;
    }

    step.value = 'operator';
};

/**
 * Update the operator code and go to the next step.
 *
 * @param code
 */
const updateOperatorCode = (code: string) => {
    if (internalOrder.value) {
        internalOrder.value.operator_code = code;
        if (!isRefused.value) {
            step.value = 'date';
        } else {
            step.value = 'reject-comment';
        }
    }
};

/**
 * Validate the selected date and go to the next step.
 *
 * @param date
 */
const validateDate = (date: string) => {
    if (internalOrder.value) {
        step.value = 'comment';
        internalOrder.value.available_at = date;
    }
};

/**
 * Validate the order with an optional comment.
 *
 * @param comment
 */
const validateOrder = (comment: string) => {
    if (internalOrder.value) {
        editingOrder.value = true;
        if (comment) {
            internalOrder.value.operator_comment = comment;
        }
        orderApi().patch(internalOrder.value.id, internalOrder.value)
            .then(() => emits('orderHandled'))
            .finally(() => editingOrder.value = false);
    }
};

/**
 * Reject the order with a comment.
 *
 * @param comment
 */
const rejectOrder = (comment: string) => {
    if (internalOrder.value) {
        editingOrder.value = true;
        const SQLFormat = 'YYYY-MM-DD HH:mm:ss';
        const data = {
            rejected_at: dayjs().format(SQLFormat),
            operator_code: internalOrder.value.operator_code,
            operator_comment: comment
        };

        orderApi().patch(internalOrder.value.id, data)
            .then(() => emits('orderHandled'))
            .finally(() => editingOrder.value = false);
    }
};
</script>
